import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CTAButton } from "@/components/CTAButton";
import { postAudit } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import { Loader2, Phone } from "lucide-react";

const POSTE_OPTIONS = [
  ...Array.from({ length: 15 }, (_, i) => `${i + 1}`),
  "Plus de 15",
];

const VARIANTS = {
  default: {
    title: "Parlons de votre informatique",
    description: "Un échange gratuit de 30 minutes, sans engagement, pour comprendre votre situation. Nous vous rappelons rapidement.",
    submit: "Réserver mon échange",
    prefill: "",
  },
  promo: {
    title: "Réserver mon audit cybersécurité",
    description: "Offre à 100 € HT au lieu de 500 €, déduits si vous souscrivez ensuite. Nous vous recontactons rapidement pour convenir d'un créneau — sans engagement.",
    submit: "Réserver mon audit",
    prefill: "Je souhaite profiter de l'offre : audit cybersécurité à 100 € HT.",
  },
};

export const AuditDialog = ({ open, onOpenChange, variant = "default" }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ nom: "", entreprise: "", siret: "", email: "", telephone: "", postes: "", message: "" });

  const content = VARIANTS[variant] || VARIANTS.default;

  // Pré-remplit le message selon le variant, sans écraser ce que l'utilisateur a déjà saisi.
  useEffect(() => {
    if (open && content.prefill) {
      setForm((f) => (f.message ? f : { ...f, message: content.prefill }));
    }
  }, [open, content.prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email || !form.postes) {
      toast.error("Merci de renseigner votre nom, votre email et le nombre de postes.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await postAudit(form);
      trackEvent("generate_lead", { method: variant === "promo" ? "audit_promo" : "echange" });
      onOpenChange(false);
      setForm({ nom: "", entreprise: "", siret: "", email: "", telephone: "", postes: "", message: "" });
      // Le calendrier Calendly s'affiche directement sur la page Merci :
      // pas de nouvelle fenêtre, donc rien que le navigateur puisse bloquer.
      navigate("/merci", { state: { calendlyUrl: (data && data.calendlyUrl) || "" } });
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous appeler au 03 20 49 02 04.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-xl" data-testid="audit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight text-navy">
            {content.title}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {content.description}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2" data-testid="audit-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="nom">Nom *</Label>
              <Input id="nom" value={form.nom} onChange={set("nom")} placeholder="Votre nom" data-testid="audit-nom" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="entreprise">Entreprise</Label>
              <Input id="entreprise" value={form.entreprise} onChange={set("entreprise")} placeholder="Nom de l'entreprise" data-testid="audit-entreprise" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="siret">SIRET <span className="text-slate-400 font-normal">(optionnel)</span></Label>
            <Input id="siret" value={form.siret} onChange={set("siret")} placeholder="14 chiffres" inputMode="numeric" data-testid="audit-siret" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} placeholder="vous@entreprise.fr" data-testid="audit-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="telephone">Téléphone</Label>
              <Input id="telephone" value={form.telephone} onChange={set("telephone")} placeholder="03 XX XX XX XX" data-testid="audit-telephone" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Nombre de postes *</Label>
            <Select value={form.postes} onValueChange={set("postes")}>
              <SelectTrigger data-testid="audit-postes"><SelectValue placeholder="Sélectionnez" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {POSTE_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="message">Message</Label>
            <Textarea id="message" value={form.message} onChange={set("message")} rows={3} placeholder="Dites-nous en quelques mots où vous en êtes." data-testid="audit-message" />
          </div>
          <CTAButton type="submit" size="lg" className="w-full" disabled={loading} data-testid="audit-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : content.submit}
          </CTAButton>
          <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
            <Phone className="w-3 h-3" /> Ou appelez-nous directement au 03 20 49 02 04
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
